import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import isBoolean from "lodash/isBoolean";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import isNumber from "lodash/isNumber";
import mapKeys from "lodash/mapKeys";

const enterpriseWorkflowApi = AiseaApiFactory.get("enterpriseWorkflow");

class EnterpriseWorkflowService {
  /**
   * APIで法人に紐づくWorkflow一覧を取得する
   * @returns {Promise<Object|null>} 操作設定のオブジェクトまたはnull
   * @property {boolean} status - APIのリクエストが成功したかどうか
   * @property {number} code - レスポンスのステータスコード
   * @property {EnterpriseWorkflow[]} data - レスポンスのデータ
   * @property {string} message - レスポンスのメッセージ
   * @throws {Error}
   */
  async getEnterpriseWorkflows() {
    return await enterpriseWorkflowApi.index();
  }

  validEnterpriseWorkflows(enterpriseWorkflows) {
    if (!Array.isArray(enterpriseWorkflows)) return [];
    return enterpriseWorkflows.filter(ew => this.isValidEnterpriseWorkflow(ew));
  }

  /**
   * Maps enterprise workflows by workflow status ID.
   *
   * @param {EnterpriseWorkflow[]} enterpriseWorkflows - The array of enterprise workflows.
   * @returns {EnterpriseWorkflowMapByWorkflowStatusId} - An object mapping workflow status IDs to enterprise workflows.
   */
  enterpriseWorkflowMapByWorkflowStatusId(enterpriseWorkflows) {
    return this.validEnterpriseWorkflows(enterpriseWorkflows).reduce(
      (acc, ew) => {
        ew.workflow_statuses
          .filter(ws => this.isEnterpriseWorkflowStatus(ws))
          .forEach(we => {
            acc[we.id] = ew;
          });
        return acc;
      },
      {}
    );
  }

  /**
   * Maps an array of workflow statuses to an object with status IDs as keys.
   *
   * @param {EnterpriseWorkflowStatus[]} workflowStatuses - The array of workflow statuses to be mapped.
   * @returns {EnterpriseWorkflowStatusMap} - An object with status IDs as keys and corresponding workflow statuses as values.
   */
  enterpriseWorkflowStatusMap(workflowStatuses) {
    return mapKeys(workflowStatuses, "id");
  }

  /**
   * Check if an EnterpriseWorkflow is valid
   * @param {EnterpriseWorkflow} workflow - The EnterpriseWorkflow object to check
   * @returns {boolean} - True if the EnterpriseWorkflow is valid, false otherwise
   */
  isValidEnterpriseWorkflow(enterpriseWorkflow) {
    const ew = enterpriseWorkflow;
    if (!isObject(ew)) return false;
    return (
      isString(ew.id) &&
      Array.isArray(ew.workflow_statuses) &&
      Array.isArray(ew.workflow_transactions)
    );
  }

  /**
   * Check if an EnterpriseWorkflowStatus is valid
   * @param {EnterpriseWorkflowStatus} status - The EnterpriseWorkflowStatus object to check
   * @returns {boolean} - True if the EnterpriseWorkflowStatus is valid, false otherwise
   */
  isEnterpriseWorkflowStatus(status) {
    const s = status;
    if (!isObject(status)) return false;
    return (
      isString(s.id) &&
      isBoolean(s.is_initial) &&
      Number.isInteger(s.sort_order) &&
      isString(s.label) &&
      isString(s.short_label) &&
      isString(s.next_action_label) &&
      isString(s.supplement_label) &&
      (isObject(s.category) || s.category === null)
    );
  }

  /**
   * Checks if the given object is a valid enterprise workflow transaction.
   *
   * @param {Object} obj - The object to be checked.
   * @returns {boolean} - Returns true if the object is a valid enterprise workflow transaction, otherwise returns false.
   */
  isEnterpriseWorkflowTransaction(obj) {
    return (
      isObject(obj) &&
      isString(obj.id) &&
      isString(obj.label) &&
      isBoolean(obj.is_progress) &&
      isString(obj.trigger) &&
      isNumber(obj.require_trigger_num) &&
      isBoolean(obj.source_workflow_global) &&
      (isString(obj.source_workflow_status_id) ||
        obj.source_workflow_status_id === null) &&
      isString(obj.target_workflow_status_id)
    );
  }

  /**
   * Checks if a transaction is in progress.
   *
   * @param {Object} transaction - The transaction object to check.
   * @returns {boolean} - Returns true if the transaction is in progress, false otherwise.
   */
  isTransactionProgress(transaction) {
    if (!this.isEnterpriseWorkflowTransaction(transaction)) return false;
    return transaction.is_progress;
  }

  /**
   * Checks if a transaction is a global transaction in the enterprise workflow.
   * @param {Object} transaction - The transaction object to check.
   * @returns {boolean} - Returns true if the transaction is a global transaction, false otherwise.
   */
  isGlobalTransaction(transaction) {
    if (!this.isEnterpriseWorkflowTransaction(transaction)) return false;
    return transaction.source_workflow_global;
  }

  /**
   * Checks if the given status is the target status of the given transaction.
   * @param {Object} status - EnterpriseWorkflowStatus
   * @param {Object} transaction - EnterpriseWorkflowTransaction
   * @returns {boolean} - Returns true if the status is target of the transaction, false otherwise.
   */
  isStatusTransactionTarget(status, transaction) {
    if (!this.isEnterpriseWorkflowStatus(status)) return false;
    if (!this.isEnterpriseWorkflowTransaction(transaction)) return false;
    return status.id === transaction.target_workflow_status_id;
  }

  /**
   * Checks if the given status is the source status of the given transaction.
   * @param {Object} status - EnterpriseWorkflowStatus
   * @param {Object} transaction - EnterpriseWorkflowTransaction
   * @returns {boolean} - Returns true if the status is source of the transaction, false otherwise.
   */
  isStatusTransactionSource(status, transaction) {
    if (!this.isEnterpriseWorkflowStatus(status)) return false;
    if (!this.isEnterpriseWorkflowTransaction(transaction)) return false;
    return status.id === transaction.source_workflow_status_id;
  }

  /**
   * Checks if there is an association between a transaction and a status.
   * @param {Object} status - The EnterpriseWorkflowStatus object.
   * @param {Object} transaction - The EnterpriseWorkflowTransaction object.
   * @returns {boolean} - Returns true if there is an association, false otherwise.
   *                      a status is source of a transaction
   *                      or
   *                      a transaction is global and a status is not target of a transaction
   */
  hasAssociationBetweenStatusAndTransaction(status, transaction) {
    return (
      this.isStatusTransactionSource(status, transaction) ||
      (this.isGlobalTransaction(transaction) &&
        !this.isStatusTransactionTarget(status, transaction))
    );
  }
}

export default new EnterpriseWorkflowService();
